import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Pill, EmptyState, ExportButton } from "./AdminUI";

const COLORS = ["#2E6B57", "#F59E0B", "#EF4444", "#94A3B8", "#3B82F6"];
const STATUSES = ["submitted", "under_review", "resolved", "dismissed", "escalated"];

export default function ModerationTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/moderation/admin/reports", { params: { ...(status ? { status } : {}), ...(type ? { target_type: type } : {}) } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [status, type]); // eslint-disable-line

  const setReportStatus = async (id, newStatus) => {
    try { await api.patch(`/moderation/admin/reports/${id}`, { status: newStatus }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const hideContent = async (id) => {
    try { await api.post(`/moderation/admin/reports/${id}/hide-content`); toast.success("Content hidden"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const restoreContent = async (id) => {
    try { await api.post(`/moderation/admin/reports/${id}/restore-content`); toast.success("Content restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <p className="text-sm text-[#222]/45">Loading…</p>;

  return (
    <div data-testid="admin-moderation-tab">
      <div className="flex justify-end mb-3"><ExportButton kind="moderation" label="Moderation history" /></div>
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <ChartCard title="Reports by type" rows={data.by_type} />
        <ChartCard title="Reports by status" rows={data.by_status} />
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <h3 className="text-sm font-bold mb-3">Most common reasons</h3>
          {data.by_reason.length === 0 ? <p className="text-sm text-[#222]/40 py-8 text-center">No reports yet</p> : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.by_reason} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#EF4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={status} onChange={e => setStatus(e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="admin-moderation-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={type} onChange={e => setType(e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="admin-moderation-type-filter">
          <option value="">All types</option>
          {["post", "comment", "account", "review", "photo", "message"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {data.reports.length === 0 ? (
        <EmptyState title="No reports" body="Reported content will show up here for review." testid="admin-moderation-empty" />
      ) : (
        <div className="space-y-3">
          {data.reports.map(r => (
            <div key={r.id} className="bg-white rounded-2xl p-4 shadow-sm border border-black/5" data-testid={`admin-report-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm font-semibold flex items-center gap-2">
                    {r.target_type} · {r.reason} <Pill tone={r.status === "resolved" ? "green" : r.status === "escalated" ? "red" : "neutral"}>{r.status}</Pill>
                  </p>
                  <p className="text-xs text-[#222]/50">Reported by {r.reporter_name} {r.reported_name && `against ${r.reported_name}`} · {new Date(r.created_at).toLocaleString()}</p>
                  {r.snapshot && <p className="text-xs text-[#222]/70 mt-1 italic">"{r.snapshot}"</p>}
                  {r.prior_related_reports > 0 && <p className="text-xs text-amber-700 mt-1">{r.prior_related_reports} prior related report(s)</p>}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <select value={r.status} onChange={e => setReportStatus(r.id, e.target.value)} className="text-xs px-2 py-1 border border-black/10 rounded-full" data-testid={`admin-report-status-${r.id}`}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {["post", "review", "photo"].includes(r.target_type) && (
                    <>
                      <button onClick={() => hideContent(r.id)} className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Hide content</button>
                      <button onClick={() => restoreContent(r.id)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold">Restore</button>
                    </>
                  )}
                  <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="text-xs px-3 py-1.5 rounded-full bg-black/5 font-semibold">{expanded === r.id ? "Hide" : "Notes"}</button>
                </div>
              </div>
              {expanded === r.id && (
                <div className="mt-3 border-t border-black/5 pt-3 text-xs text-[#222]/60 space-y-1">
                  {r.admin_notes?.length ? r.admin_notes.map((n, i) => <p key={i}>{n.by}: {n.text}</p>) : <p className="text-[#222]/35">No notes yet</p>}
                  {r.resolution && <p className="mt-1"><b>Resolution:</b> {r.resolution} ({r.resolved_by})</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, rows }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
      <h3 className="text-sm font-bold mb-3">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-[#222]/40 py-8 text-center">No data yet</p> : (
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} label>
              {rows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
